import React from 'react';
import styled, { css } from 'styled-components';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import { shimmerText } from './activityShimmer';
import type { TodoListContentItem, TodoTask } from '../../shared/interfaces';

// Checklist mantido pelo modelo (lista_de_tarefas). Cada chamada da tool
// substitui a lista, então a mensagem mostra uma única versão, a mais recente.

const Card = styled.div`
  margin: 8px 0;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.text_secondary}33;
  background: ${({ theme }) => theme.user_message_background};
  font-family: 'Sora', 'Lexend', sans-serif;
  max-width: 560px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.text_secondary};
  margin-bottom: 6px;
`;

const Item = styled.div<{ $status: TodoTask['status']; $live: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.9rem;
  line-height: 1.5rem;
  color: ${({ theme }) => theme.font_color};
  ${({ $status }) => $status === 'concluida' && css`
    opacity: 0.6;
    text-decoration: line-through;
  `}
  ${({ $status, $live }) => $status === 'em_andamento' && $live && css`
    ${shimmerText}
  `}
`;

const ICON: Record<TodoTask['status'], React.ReactNode> = {
  concluida: <CheckCircleRoundedIcon style={{ fontSize: 18, marginTop: 3, color: '#2e9e5b' }} />,
  em_andamento: <PlayCircleOutlineRoundedIcon style={{ fontSize: 18, marginTop: 3 }} />,
  pendente: <RadioButtonUncheckedRoundedIcon style={{ fontSize: 18, marginTop: 3, opacity: 0.6 }} />,
};

interface Props {
  data: TodoListContentItem;
  // Turno ainda em geração: a tarefa em andamento ganha o shimmer.
  live?: boolean;
}

function TodoListBlock({ data, live }: Props) {
  const tarefas = data.tarefas || [];
  const done = tarefas.filter((t) => t.status === 'concluida').length;
  return (
    <Card data-testid="todo-list-block" data-download-exclude>
      <Header>
        <ChecklistRoundedIcon style={{ fontSize: 16 }} />
        <span>Tarefas · {done}/{tarefas.length} concluída{done === 1 ? '' : 's'}</span>
      </Header>
      {tarefas.map((t, i) => (
        <Item key={i} $status={t.status} $live={!!live}>
          {ICON[t.status] || ICON.pendente}
          <span>{t.titulo}</span>
        </Item>
      ))}
    </Card>
  );
}

export default TodoListBlock;
